import { ConditionExpressionType } from "./ConditionExpressionType";

/**
 * Everything the UI needs to say about a condition expression, in one place,
 * keyed by the type that is persisted and evaluated. The three forms are how the
 * platform renders one -- a phrase, a symbol, a title-case label -- and the flags
 * are what an operator selector needs to decide which expressions it can offer.
 */
export interface ConditionExpressionMeta {
    /** Lowercase natural-language form, e.g. `does not equal`. */
    phrase: string;
    /** Compact form for summarizing a condition inline, e.g. `≠`. */
    symbol: string;
    /** Title-case form, for an operator dropdown. */
    label: string;
    /**
     * False for the presence checks, which test the property on its own and have
     * nothing to compare against.
     */
    takesValue: boolean;
    /**
     * True for the substring comparisons, which only mean something for free
     * text -- not for a boolean or an enumerated value.
     */
    isStringComparison: boolean;
}

/**
 * Declaration order is the order an operator selector lists these in. Adding a
 * member to `ConditionExpressionType` without describing it here is a compile
 * error, which is the point: every selector and summary derives from this table.
 */
export const conditionExpressions = {
    [ConditionExpressionType.Equal]: {
        phrase: "equals",
        symbol: "=",
        label: "Equal",
        takesValue: true,
        isStringComparison: false,
    },
    [ConditionExpressionType.NotEqual]: {
        phrase: "does not equal",
        symbol: "≠",
        label: "Not Equal",
        takesValue: true,
        isStringComparison: false,
    },
    [ConditionExpressionType.ContainsString]: {
        phrase: "contains",
        symbol: "contains",
        label: "Contains text",
        takesValue: true,
        isStringComparison: true,
    },
    [ConditionExpressionType.DoesNotContainString]: {
        phrase: "does not contain",
        symbol: "does not contain",
        label: "Does not contain text",
        takesValue: true,
        isStringComparison: true,
    },
    [ConditionExpressionType.Exists]: {
        phrase: "is defined",
        symbol: "is defined",
        label: "Is defined",
        takesValue: false,
        isStringComparison: false,
    },
    [ConditionExpressionType.DoesNotExist]: {
        phrase: "is not defined",
        symbol: "is not defined",
        label: "Is not defined",
        takesValue: false,
        isStringComparison: false,
    },
} as const satisfies Record<ConditionExpressionType, ConditionExpressionMeta>;

/** The natural-language forms, as a union rather than a bare string. */
export type ConditionExpressionPhrase =
    (typeof conditionExpressions)[ConditionExpressionType]["phrase"];

export interface ConditionExpressionEntry extends ConditionExpressionMeta {
    type: ConditionExpressionType;
    phrase: ConditionExpressionPhrase;
}

/** Every expression type with its metadata, in the order declared above. */
export const orderedConditionExpressions: ConditionExpressionEntry[] = (
    Object.keys(conditionExpressions) as ConditionExpressionType[]
).map((type) => ({ type, ...conditionExpressions[type] }));

/** The same entries, keyed by phrase, for looking one up from its spoken form. */
export const conditionExpressionByPhrase = Object.fromEntries(
    orderedConditionExpressions.map((entry) => [entry.phrase, entry]),
) as Record<ConditionExpressionPhrase, ConditionExpressionEntry>;

/**
 * `Exists` and `DoesNotExist` test the property alone, with nothing to compare
 * to, so whatever renders a value field has to leave it out for those.
 */
export function expressionTakesValue(type: ConditionExpressionType): boolean {
    return conditionExpressions[type].takesValue;
}
